import { FunctionComponent } from 'react';

import {
  AdvisorEntity,
  AnnuitantEntity,
  Application,
  ApplicationPointOfSaleDecisions,
  ComprehensivePricing,
  DynamicPdfTextOverrides,
  DynamicPdfType,
  ESignSigner,
  IsSmoker,
  Language,
  Localizable,
  OwnerEntity,
  PayerEntity,
  PdfOptions,
  ProductAddon,
  ProductsEntity,
  ProposedInsuredEntity,
  SignatureFieldType,
} from '@breathelife/types';
import {
  IsoCountryCode,
  NodeIdToAnswerPathMap,
  ParticipantRole,
  PdfDocumentType,
  PdfDocumentTypes,
  RenderingField,
  RenderingQuestionnaire,
} from '@zinnia/form-engine-sdk';

// OneSpan Text Tag Field Type
export enum AutofieldFieldType {
  signerName = 'signerName',
  signature = 'signature',
  signerTitle = 'signerTitle',
  signerCompany = 'signerCompany',
  signerDate = 'signerDate',
}

// OneSpan Text Tag Field Type
export enum InputFieldType {
  textField = 'textField',
  textArea = 'textArea',
  list = 'list',
  radio = 'radio',
  checkbox = 'checkbox',
  label = 'label',
}

export type SignerDetails = {
  title?: Localizable;
  text?: Localizable;
};

export type SignatureDetails = Partial<Record<ParticipantRole, SignerDetails>>;

export type Signers = Partial<Record<ParticipantRole, ESignSigner[]>>;

export type DynamicPdfProps = {
  carrierPdfOptions: PdfOptions | null;
  insuredPdfOptions: PdfOptions | null;
  advisorPdfOptions: PdfOptions | null;
  medicalInterviewAnswersPdfOptions: PdfOptions | null;
  padPdfOptions: PdfOptions | null;
  countryCode: IsoCountryCode;
  pdfTextOverrides?: DynamicPdfTextOverrides;
  signatureOptions?: { eSignatureFieldType?: SignatureFieldType };
};

export type PdfData = {
  renderingQuestionnaire: RenderingQuestionnaire;
  nodeIdToAnswerPathMap: NodeIdToAnswerPathMap;
  application: Application;
  currency: string;
  cryptoSignatureData?: CryptoSignatureData;

  // TODO: update signees or replace type with entity structure
  signatureData?: SignatureData;
  proposedInsuredInformation?: ProposedInsuredEntity[];
  productsEntity?: ProductsEntity;
  advisors?: AdvisorEntity[];
  addons?: ProductAddon[];
  annuitantInformation?: AnnuitantEntity | null;
  // Use applicantInformation for fields that represent insured or annuitant depending on line of business
  applicantInformation?: ProposedInsuredEntity[] | AnnuitantEntity | null;
  payorInformation?: PayerEntity[];
  ownerInformation?: OwnerEntity[];
  pdfType: DynamicPdfType;
  pointOfSaleDecisions?: ApplicationPointOfSaleDecisions | null;
  isSmoker: IsSmoker;
  comprehensivePricing?: ComprehensivePricing;
  participantWithMedicalInterviewAnswers?: { fullName: string; answers: RenderingQuestionnaire };
};

export type CryptoSignatureData = {
  signature?: string;
  date?: Date;
  payerIdentifier?: string;
};

export type SignatureData = {
  signers: Signers;
  signatureDetails?: SignatureDetails;
  isExternalSignature: boolean;
  eSignatureFieldType?: SignatureFieldType;
};

export type ReactElementList = Partial<Record<string, FunctionComponent>>;

export type RenderedFieldProps = {
  field: RenderingField;
  label: string;
  locale?: Language;
  subtitle?: string;
  fieldIndex?: string;
};

export const pdfTypeLookup: Record<DynamicPdfType, PdfDocumentType> = {
  [DynamicPdfType.advisor]: PdfDocumentTypes.advisorsReport,
  [DynamicPdfType.carrier]: PdfDocumentTypes.carrierReport,
  [DynamicPdfType.insured]: PdfDocumentTypes.application,
  [DynamicPdfType.medicalInterviewAnswers]: PdfDocumentTypes.medicalInterviewAnswers,
  [DynamicPdfType.pad]: PdfDocumentTypes.pad,
};
